import React, { useState, useEffect, useMemo } from 'react';
import { Avatar, Image } from 'antd';
import { UserOutlined, LoadingOutlined } from '@ant-design/icons';
import api from '../utils/api';
import imageCache from '../utils/imageCache';

/**
 * UnknownFaceAvatar Component
 * Displays unknown face photo with optional preview functionality and image caching
 *
 * @param {string} unknownId - Unknown face ID
 * @param {number} photoIndex - Photo index (default: 0)
 * @param {number|string} size - Avatar size (default: 40)
 * @param {object} style - Additional styles for avatar
 * @param {boolean} enablePreview - Enable click-to-preview functionality (default: false)
 */
const UnknownFaceAvatar = ({ unknownId, photoIndex = 0, size = 40, style = {}, enablePreview = false }) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [cachedImageUrl, setCachedImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  // Memoize the image URL to prevent unnecessary re-calculations
  const imageUrl = useMemo(() => {
    if (!unknownId) return null;
    return api.getUnknownPhoto(unknownId, photoIndex);
  }, [unknownId, photoIndex]);

  useEffect(() => {
    if (!imageUrl) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadCachedImage = async () => {
      try {
        setLoading(true);
        const cached = await imageCache.get(imageUrl);

        if (!cancelled) {
          setCachedImageUrl(cached);
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to load cached image:', error);
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCachedImage();

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  if (!unknownId || loading) {
    return (
      <Avatar
        size={size}
        icon={loading ? <LoadingOutlined spin /> : <UserOutlined />}
        style={style}
      />
    );
  }

  const avatarElement = (
    <Avatar
      size={size}
      src={cachedImageUrl}
      icon={<UserOutlined />}
      style={{
        ...style,
        cursor: enablePreview ? 'pointer' : 'default'
      }}
      onClick={() => enablePreview && setPreviewVisible(true)}
    />
  );

  if (!enablePreview) {
    return avatarElement;
  }

  return (
    <>
      {avatarElement}
      <Image
        preview={{
          visible: previewVisible,
          src: cachedImageUrl,
          onVisibleChange: (visible) => setPreviewVisible(visible),
        }}
        style={{ display: 'none' }}
      />
    </>
  );
};

export default UnknownFaceAvatar;
